import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ContactForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organisation: '',
    country: '',
    phone: '',
    interests: [],
    objectives: [],
    message: '',
  });

  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        submittedAt: new Date().toISOString(),
      };

      const response = await axios.post('/api/contact', payload);

      toast({
        title: 'Success!',
        description: response.data.message,
      });

      setFormData({
        fullName: '',
        email: '',
        organisation: '',
        country: '',
        phone: '',
        interests: [],
        objectives: [],
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.detail ||
          'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 medical-section-bg"
      style={{ backgroundImage: `images/contactform/contactform-1.jpg` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className="text-4xl font-bold mb-4"
            style={{ color: '#2D4A8F' }}
          >
            {t.contact.title}
          </h2>
          <p className="text-lg text-gray-600">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="images/contactform/contactform-1.jpg"
                alt="Medical consultation"
                className="w-full h-full object-cover"
                style={{ borderRadius: '24px' }}
              />
            </div>
            <div className="mt-6 p-6 rounded-xl bg-white shadow-lg">
              <div className="flex items-center space-x-3 mb-3">
                <CheckCircle2 size={24} style={{ color: '#2D4A8F' }} />
                <span
                  className="font-semibold"
                  style={{ color: '#2D4A8F' }}
                >
                  {t.contact.gdprNote}
                </span>
              </div>
              <p className="text-gray-600">{t.contact.gdprDescription}</p>
            </div>
          </div>

          {/* Right - Form */}
          <Card className="border-none shadow-2xl">
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="fullName"
                    className="text-gray-700 font-medium"
                  >
                    {t.contact.form.fullName} *
                  </Label>
                  <Input
                    id="fullName"
                    required
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-gray-700 font-medium"
                  >
                    {t.contact.form.email} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="organisation"
                    className="text-gray-700 font-medium"
                  >
                    {t.contact.form.organisation} *
                  </Label>
                  <Input
                    id="organisation"
                    required
                    value={formData.organisation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        organisation: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="country"
                    className="text-gray-700 font-medium"
                  >
                    {t.contact.form.country} *
                  </Label>
                  <Input
                    id="country"
                    required
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label
                  htmlFor="phone"
                  className="text-gray-700 font-medium"
                >
                  {t.contact.form.phone}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="mt-1"
                />
              </div>

              {/* Interests */}
              <div>
                <Label className="text-gray-700 font-medium mb-3 block">
                  {t.contact.form.interests}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {t.contact.interestOptions.map((option, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`interest-${idx}`}
                        checked={formData.interests.includes(option)}
                        onCheckedChange={() =>
                          handleCheckboxChange('interests', option)
                        }
                      />
                      <label
                        htmlFor={`interest-${idx}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Objectives */}
              <div>
                <Label className="text-gray-700 font-medium mb-3 block">
                  {t.contact.form.objectives}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {t.contact.objectiveOptions.map((option, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`objective-${idx}`}
                        checked={formData.objectives.includes(option)}
                        onCheckedChange={() =>
                          handleCheckboxChange('objectives', option)
                        }
                      />
                      <label
                        htmlFor={`objective-${idx}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label
                  htmlFor="message"
                  className="text-gray-700 font-medium"
                >
                  {t.contact.form.message}
                </Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="mt-1"
                  placeholder={t.contact.form.messagePlaceholder}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-lg text-white font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2D4A8F' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>{t.contact.form.sending}</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>{t.contact.form.submit}</span>
                  </>
                )}
              </button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
